import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useYacht } from "../../context/YachtContext";
import { useAuth } from "../../context/AuthContext";
import { api, fmtErr } from "../../lib/api";
import { limitToast } from "../../lib/limits";
import { PageHeader } from "./Dashboard";
import { Field, Th, Td, EmptyYacht, resizeImageToDataUrl } from "./Inventory";
import { Plus, Trash2, Wallet, Check, X, Pencil, UserCog, ShieldCheck, AlertTriangle, Camera } from "lucide-react";
import { AuditTimeline } from "../../components/AuditTimeline";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import FileUploader, { AuthImage, AuthFileLink, PdfPreview } from "../../components/FileUploader";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import ConfirmDialog from "../../components/ConfirmDialog";
import ImageLightbox from "../../components/ImageLightbox";
import { toast } from "sonner";
import { useRights } from "../../lib/useRights";
import { useTour } from "../../components/tour/TourContext";
import TourReplayButton from "../../components/tour/TourReplayButton";

const CATEGORIES = ["Fuel", "Transport", "Cleaning supplies", "Food and drinks", "Spare parts", "Tools", "Tips", "Marina fees", "Customs/permits", "Laundry", "Repairs", "Other"];
const SENIOR_ROLES = new Set(["owner", "manager"]);

const DEFAULT = { type: "expense", amount: "", currency: "AED", category: CATEGORIES[0], description: "", paid_by: "", receipt_path: "" };

// Translate the (status, type) pair into a label + tone. New states:
//   pending_receipt — custodian still has to acknowledge the cash transfer
//   received        — custodian confirmed they got the cash
const STATUS_TONE = {
  pending:          { label: "pending",          cls: "bg-amber-50 text-amber-700 border-amber-200" },
  pending_receipt:  { label: "awaiting receipt", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  approved:         { label: "approved",         cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  received:         { label: "received",         cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rejected:         { label: "rejected",         cls: "bg-red-50 text-red-700 border-red-200" },
};

function StatusPill({ value }) {
  const tone = STATUS_TONE[value] || { label: value, cls: "bg-zinc-100 text-zinc-700 border-zinc-200" };
  return (
    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border font-medium whitespace-nowrap ${tone.cls}`}>
      {tone.label}
    </span>
  );
}

export default function PettyCash() {
  const { t } = useTranslation();
  const { activeYacht } = useYacht();
  const { user } = useAuth();
  const { can } = useRights();
  const { activeTour } = useTour();
  const [data, setData] = useState({ items: [], balance: 0, custodian_id: null, custodian_name: null });
  // Current user's role on this yacht — needed locally to gate approval/
  // custodian-management UI without querying useRights for every action.
  const [myRole, setMyRole] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(DEFAULT);
  // Either the row being inspected (details modal) or the row being edited.
  const [detail, setDetail] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(DEFAULT);
  // Delete-confirm gate — protects against accidental taps on the red
  // button in the detail modal. Same pattern we already use for Tasks,
  // Checklists, Logbook, etc.
  const [toDelete, setToDelete] = useState(null);
  const [delBusy, setDelBusy] = useState(false);
  // Custodian picker dialog state.
  const [custodianDlg, setCustodianDlg] = useState(false);

  const isCustodian = !!data.custodian_id && data.custodian_id === user?.id;
  const isSenior = SENIOR_ROLES.has((myRole || "").toLowerCase());

  const load = async () => {
    if (!activeYacht?.id) return;
    try {
      const { data } = await api.get(`/petty-cash?yacht_id=${activeYacht.id}`);
      setData(data);
    } catch {
      setData({ items: [], balance: 0, custodian_id: null, custodian_name: null });
    }
  };
  // Resolve our role on this yacht from the rights endpoint so we know whether
  // to show the "Change custodian" + senior-only actions. Cheaper than a full
  // members query.
  const loadMyRole = async () => {
    if (!activeYacht?.id) { setMyRole(null); return; }
    try {
      const { data: r } = await api.get(`/yachts/${activeYacht.id}/rights`);
      setMyRole(r?.role || null);
    } catch { setMyRole(null); }
  };

  useEffect(() => {
    setData({ items: [], balance: 0, custodian_id: null, custodian_name: null });
    setMyRole(null);
    load();
    loadMyRole();
    /* eslint-disable-next-line */
  }, [activeYacht?.id]);

  // Pre-fill Paid by with the current user's own name when opening the
  // create dialog.
  useEffect(() => {
    if (!open) return;
    setForm((f) => (f.paid_by ? f : { ...f, paid_by: user?.name || user?.email || "" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const save = async () => {
    try {
      await api.post("/petty-cash", { ...form, yacht_id: activeYacht.id, amount: Number(form.amount || 0) });
      toast.success("Entry added"); setForm(DEFAULT); setOpen(false); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  // "Scan receipt" flow: photograph/upload a receipt → AI reads amount,
  // currency, category and merchant and prefills the entry; the photo is
  // uploaded as the attached receipt in parallel. User just checks + saves.
  const scanInputRef = useRef(null);
  const handleScanReceipt = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
    let payload;
    if (isPdf) {
      if (file.size > 12 * 1024 * 1024) {
        toast.error("PDF too large — max 12 MB");
        return;
      }
      const dataUrl = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = rej;
        r.readAsDataURL(file);
      }).catch(() => null);
      if (!dataUrl) { toast.error("Could not read that PDF — try another file"); return; }
      payload = { pdf_base64: dataUrl };
    } else {
      let scanUrl = "";
      try {
        scanUrl = await resizeImageToDataUrl(file, 1400);
      } catch {
        toast.error("Could not read that photo — try another one");
        return;
      }
      payload = { image_base64: scanUrl };
    }
    setForm({ ...DEFAULT, paid_by: user?.name || user?.email || "" });
    setOpen(true);
    toast.message("Reading receipt with AI…");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("yacht_id", activeYacht.id);
    fd.append("folder", "receipts");
    api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } })
      .then(({ data }) => setForm((p) => ({ ...p, receipt_path: data.storage_path })))
      .catch(() => toast.warning("Receipt couldn't be attached — attach it manually"));
    try {
      const { data } = await api.post("/petty-cash/ai-scan", payload);
      setForm((prev) => ({
        ...prev,
        amount: prev.amount || (data.amount ?? ""),
        currency: data.currency || prev.currency,
        category: data.category || prev.category,
        description: prev.description || data.description || "",
        date: data.date || prev.date,
      }));
      if (data.amount != null) {
        toast.success(`Scanned: ${data.currency || ""} ${data.amount}${data.merchant ? ` · ${data.merchant}` : ""} — check & save`);
      } else {
        toast.warning("Couldn't read the receipt — fill the details manually");
      }
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      await api.patch(`/petty-cash/${editing.id}`, {
        ...editForm, yacht_id: activeYacht.id, amount: Number(editForm.amount || 0),
      });
      toast.success("Entry updated"); setEditing(null); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const approve = async (id, status) => {
    try { await api.post(`/petty-cash/${id}/approve`, { status }); load(); }
    catch (e) {
      const it = (data.items || []).find((x) => x.id === id);
      if (!limitToast(e, it && { yacht_id: it.yacht_id || activeYacht?.id, kind: "pettycash", ref_id: id, label: it.description || it.category || "", amount: it.amount })) {
        toast.error(fmtErr(e));
      }
    }
  };
  const acknowledge = async (id, status) => {
    try { await api.post(`/petty-cash/${id}/acknowledge`, { status }); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };
  const performDelete = async () => {
    if (!toDelete) return;
    setDelBusy(true);
    try {
      await api.delete(`/petty-cash/${toDelete.id}`);
      toast.success("Entry deleted");
      setToDelete(null);
      setDetail(null);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setDelBusy(false);
    }
  };

  // Whether the current user is allowed to approve/reject this expense.
  // Mirrors the backend rule so the buttons don't appear if the server would
  // reject the call.
  const canApproveExpense = (it) => {
    if (it.type !== "expense" || it.status !== "pending") return false;
    if (it.created_by === user?.id) return false;
    if (isSenior) return true;
    if (data.custodian_id && data.custodian_id === user?.id) return true;
    // No custodian set → legacy rights matrix applies.
    if (!data.custodian_id && can("pettycash:approve")) return true;
    return false;
  };
  const canAcknowledgeTopup = (it) =>
    it.type === "topup" && it.status === "pending_receipt" && isCustodian;
  const canEditEntry = (it) => {
    // Submitters can fix typos until the entry is finalised; seniors can
    // patch any pending entry on the yacht (charter-accounting reality —
    // the captain often spots a wrong amount the deckhand logged). The
    // backend mirrors this exact rule.
    if (!(it.status === "pending" || it.status === "pending_receipt")) return false;
    if (it.created_by === user?.id) return true;
    return can("pettycash:approve");  // owner / manager / captain
  };
  const canDelete = (it) => canEditEntry(it) || can("pettycash:delete");

  if (!activeYacht) return <EmptyYacht />;

  const pendingTopupsForMe = data.items.filter(
    (it) => it.type === "topup" && it.status === "pending_receipt" && isCustodian,
  );

  return (
    <div className="p-3 sm:p-6 lg:p-10 max-w-7xl">
      <PageHeader
        overline={activeYacht.name}
        title={t("petty_cash.title")}
        right={
          can("pettycash:create") ? (
          <div className="flex items-center gap-2">
            <TourReplayButton tourId="petty-cash" />
            <input
              ref={scanInputRef}
              type="file"
              accept="image/*,application/pdf"
              className="hidden"
              onChange={handleScanReceipt}
              data-testid="pc-scan-input"
            />
            <Button
              variant="outline"
              onClick={() => scanInputRef.current?.click()}
              data-testid="pc-scan-receipt-btn"
            >
              <Camera className="h-4 w-4 mr-2" />Scan receipt
            </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-ink hover:bg-black" data-testid="add-pc-btn"><Plus className="h-4 w-4 mr-2" />{t("petty_cash.new_topup")}</Button>
            </DialogTrigger>
            <DialogContent
              className="max-h-[90vh] overflow-y-auto"
              onPointerDownOutside={(e) => { if (activeTour) e.preventDefault(); }}
              onInteractOutside={(e) => { if (activeTour) e.preventDefault(); }}
              onEscapeKeyDown={(e) => { if (activeTour) e.preventDefault(); }}
              onOpenAutoFocus={(e) => { if (activeTour) e.preventDefault(); }}
            >
              <DialogHeader><DialogTitle>{t("petty_cash.new_topup")}</DialogTitle></DialogHeader>
              <PettyCashFormFields form={form} setForm={setForm} yacht_id={activeYacht.id} />
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={save} className="bg-ink hover:bg-black" disabled={!form.amount} data-testid="save-pc">Add entry</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
          ) : null
        }
      />

      {/* Custodian banner — always visible so the crew know who's holding
          the cash. Senior crew can re-appoint inline. */}
      <CustodianBanner
        custodianName={data.custodian_name}
        custodianId={data.custodian_id}
        isSenior={isSenior}
        onChange={() => setCustodianDlg(true)}
        currentUserId={user?.id}
      />

      {pendingTopupsForMe.length > 0 && (
        <div className="mt-4 rounded-md border border-sky-200 bg-sky-50 p-4" data-testid="pending-topups-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-sky-600 mt-0.5 shrink-0" />
            <div>
              <div className="text-sm font-medium text-sky-900">
                {pendingTopupsForMe.length} top-up{pendingTopupsForMe.length === 1 ? "" : "s"} awaiting your receipt
              </div>
              <div className="text-xs text-sky-700 mt-0.5">
                Confirm you received the cash, or reject if something is off.
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 min-[420px]:grid-cols-3 gap-2 sm:gap-3" data-tour="pc-tiles">
        <div className="rounded-md border border-zinc-200 bg-white p-3 sm:p-5">
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.16em] sm:tracking-[0.2em] text-zinc-500">Current balance</div>
          <div className="mt-1.5 sm:mt-2 font-display text-lg sm:text-3xl font-medium leading-tight" data-testid="pc-balance">AED {data.balance.toFixed(2)}</div>
        </div>
        <div className="rounded-md border border-zinc-200 bg-white p-3 sm:p-5">
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.16em] sm:tracking-[0.2em] text-zinc-500">Entries</div>
          <div className="mt-1.5 sm:mt-2 font-display text-lg sm:text-3xl font-medium leading-tight">{data.items.length}</div>
        </div>
        <div className="rounded-md border border-zinc-200 bg-white p-3 sm:p-5">
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.16em] sm:tracking-[0.2em] text-zinc-500">Pending</div>
          <div className="mt-1.5 sm:mt-2 font-display text-lg sm:text-3xl font-medium leading-tight">
            {data.items.filter((i) => i.status === "pending" || i.status === "pending_receipt").length}
          </div>
        </div>
      </div>

      {/* Desktop table (≥ md). Mobile users see the card-list below. */}
      <div className="mt-4 rounded-md border border-zinc-200 bg-white overflow-hidden hidden md:block">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-zinc-500 text-xs uppercase tracking-wider">
            <tr><Th>Date</Th><Th>Type</Th><Th>Category</Th><Th className="text-right">Amount</Th><Th>Status</Th><Th /></tr>
          </thead>
          <tbody className="divide-y divide-zinc-200">
            {data.items.length === 0 && (
              <tr><td colSpan={6} className="p-10 text-center text-zinc-500">
                <Wallet className="h-6 w-6 mx-auto text-zinc-300" />
                <div className="mt-2">No entries yet.</div>
              </td></tr>
            )}
            {data.items.map((it) => (
              <tr
                key={it.id}
                data-testid={`pc-row-${it.id}`}
                onClick={() => setDetail(it)}
                className="cursor-pointer hover:bg-zinc-50 transition-colors"
              >
                <Td className="text-xs">
                  <div className="text-zinc-600">{(it.date || it.created_at || "").slice(0, 10)}</div>
                  {it.created_by_name && (
                    <div className="text-[10px] text-zinc-400 mt-0.5">by {it.created_by_name}</div>
                  )}
                </Td>
                <Td>{it.type === "topup" ? <span className="text-emerald-700">Top-up</span> : <span>Expense</span>}</Td>
                <Td className="text-zinc-600">{it.category}</Td>
                <Td className="text-right font-mono">{it.currency} {it.amount.toFixed(2)}</Td>
                <Td><StatusPill value={it.status} /></Td>
                <Td className="text-right whitespace-nowrap">
                  {/* Senior approval/acknowledge actions stay inline as a
                      one-tap shortcut for the most common workflow (the
                      crew submits, the captain approves from the list).
                      onClick stops propagation so the row-click doesn't
                      also fire the detail modal. */}
                  {canApproveExpense(it) && (
                    <>
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); approve(it.id, "approved"); }} data-testid={`approve-${it.id}`} title="Approve"><Check className="h-4 w-4 text-emerald-600" /></Button>
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); approve(it.id, "rejected"); }} data-testid={`reject-${it.id}`} title="Reject"><X className="h-4 w-4 text-red-600" /></Button>
                    </>
                  )}
                  {canAcknowledgeTopup(it) && (
                    <>
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); acknowledge(it.id, "received"); }} data-testid={`ack-received-${it.id}`} title="Mark received"><Check className="h-4 w-4 text-emerald-600" /></Button>
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); acknowledge(it.id, "rejected"); }} data-testid={`ack-reject-${it.id}`} title="Reject"><X className="h-4 w-4 text-red-600" /></Button>
                    </>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile card list (< md). Each row becomes a tappable card — easier
          to read on a 390 px viewport than a horizontally-scrolling table.
          Approve/reject/edit actions live in a wrapping row below the
          headline numbers so they never overflow off-screen. */}
      <div className="mt-4 space-y-2 md:hidden">
        {data.items.length === 0 ? (
          <div className="rounded-md border border-zinc-200 bg-white p-8 text-center text-zinc-500" data-testid="pc-empty-mobile">
            <Wallet className="h-6 w-6 mx-auto text-zinc-300" />
            <div className="mt-2 text-sm">No entries yet.</div>
          </div>
        ) : data.items.map((it) => (
          <PettyCashMobileCard
            key={it.id}
            it={it}
            onOpen={() => setDetail(it)}
            onApprove={canApproveExpense(it) ? (s) => approve(it.id, s) : null}
            onAcknowledge={canAcknowledgeTopup(it) ? (s) => acknowledge(it.id, s) : null}
          />
        ))}
      </div>

      <PettyCashDetailDialog
        entry={detail}
        onClose={() => setDetail(null)}
        canDelete={detail ? canDelete(detail) : false}
        canEdit={detail ? canEditEntry(detail) : false}
        onEdit={() => {
          if (!detail) return;
          const it = detail;
          setDetail(null);
          setEditing(it);
          setEditForm({ ...DEFAULT, ...it });
        }}
        onDelete={() => { if (detail) setToDelete(detail); }}
      />

      <ConfirmDialog
        open={!!toDelete}
        title="Delete petty-cash entry?"
        description={
          toDelete
            ? `This will permanently remove the ${toDelete.type === "topup" ? "top-up" : "expense"} of ${toDelete.currency} ${Number(toDelete.amount).toFixed(2)}${toDelete.category ? ` (${toDelete.category})` : ""}. The deletion is recorded in the audit history.`
            : ""
        }
        confirmLabel="Yes, delete"
        destructive
        busy={delBusy}
        onCancel={() => { if (!delBusy) setToDelete(null); }}
        onConfirm={performDelete}
      />

      {/* Edit dialog — re-uses the same field set as create. Disabled when
          editing isn't allowed (status flipped server-side under us). */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="pc-edit-dialog">
          <DialogHeader><DialogTitle>Edit petty cash entry</DialogTitle></DialogHeader>
          {editing && (
            <PettyCashFormFields form={editForm} setForm={setEditForm} yacht_id={activeYacht.id} disableType />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveEdit} className="bg-ink hover:bg-black" disabled={!editForm.amount} data-testid="save-pc-edit">Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CustodianDialog
        open={custodianDlg}
        onClose={() => setCustodianDlg(false)}
        yachtId={activeYacht.id}
        currentCustodianId={data.custodian_id}
        onSaved={(next) => {
          setData((d) => ({ ...d, custodian_id: next.custodian_id, custodian_name: next.custodian_name }));
          setCustodianDlg(false);
          load();
        }}
      />
    </div>
  );
}

// ─── Mobile row card ─────────────────────────────────────────────────────
// On narrow viewports the 6-column table overflows horizontally. We swap
// it for a stacked card that shows the same information vertically and
// keeps every action button tappable (44 px touch target). Senior actions
// (approve / reject / acknowledge) wrap to a second row so the card width
// never blows out.
function PettyCashMobileCard({ it, onOpen, onApprove, onAcknowledge }) {
  const dateStr = (it.date || it.created_at || "").slice(0, 10);
  return (
    <div
      className="rounded-md border border-zinc-200 bg-white p-3 cursor-pointer hover:border-zinc-300 active:bg-zinc-50 transition-colors"
      data-testid={`pc-card-${it.id}`}
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); } }}
    >
      <div className="w-full text-left flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-zinc-500">{dateStr}</span>
            {it.type === "topup"
              ? <span className="text-[10px] uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-full">Top-up</span>
              : <span className="text-[10px] uppercase tracking-wider text-zinc-600 bg-zinc-50 border border-zinc-200 px-1.5 py-0.5 rounded-full">Expense</span>}
            <StatusPill value={it.status} />
          </div>
          <div className="mt-1 text-sm font-medium truncate">{it.category || "—"}</div>
          {it.description && (
            <div className="text-xs text-zinc-500 line-clamp-1">{it.description}</div>
          )}
          {it.created_by_name && (
            <div className="text-[10px] text-zinc-400 mt-0.5">by {it.created_by_name}</div>
          )}
        </div>
        <div className="text-right shrink-0">
          <div className="font-mono text-sm whitespace-nowrap">
            {it.currency} {Number(it.amount).toFixed(2)}
          </div>
        </div>
      </div>

      {(onApprove || onAcknowledge) && (
        <div className="mt-3 pt-3 border-t border-zinc-100 flex flex-wrap gap-2">
          {onApprove && (
            <>
              <Button
                variant="outline" size="sm"
                onClick={(e) => { e.stopPropagation(); onApprove("approved"); }}
                className="h-8 text-xs text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                data-testid={`pc-card-approve-${it.id}`}
              >
                <Check className="h-3.5 w-3.5 mr-1" /> Approve
              </Button>
              <Button
                variant="outline" size="sm"
                onClick={(e) => { e.stopPropagation(); onApprove("rejected"); }}
                className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50"
                data-testid={`pc-card-reject-${it.id}`}
              >
                <X className="h-3.5 w-3.5 mr-1" /> Reject
              </Button>
            </>
          )}
          {onAcknowledge && (
            <>
              <Button
                variant="outline" size="sm"
                onClick={(e) => { e.stopPropagation(); onAcknowledge("received"); }}
                className="h-8 text-xs text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                data-testid={`pc-card-received-${it.id}`}
              >
                <Check className="h-3.5 w-3.5 mr-1" /> Received
              </Button>
              <Button
                variant="outline" size="sm"
                onClick={(e) => { e.stopPropagation(); onAcknowledge("rejected"); }}
                className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50"
                data-testid={`pc-card-ack-reject-${it.id}`}
              >
                <X className="h-3.5 w-3.5 mr-1" /> Reject
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Reusable field group ────────────────────────────────────────────────
function PettyCashFormFields({ form, setForm, yacht_id, disableType = false }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <Field label="Type">
        <select
          className="h-9 w-full rounded-md border border-input px-3 text-sm disabled:bg-zinc-100"
          value={form.type}
          onChange={(e) => setForm({ ...form, type: e.target.value })}
          data-testid="pc-type"
          disabled={disableType}
        >
          <option value="expense">Expense</option>
          <option value="topup">Top-up</option>
        </select>
      </Field>
      <Field label="Amount *"><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="pc-amount" /></Field>
      <Field label="Currency"><Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} /></Field>
      <Field label="Category">
        <select className="h-9 w-full rounded-md border border-input px-3 text-sm" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="pc-category">
          {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
        </select>
      </Field>
      <Field label="Description" className="sm:col-span-2"><Input value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
      <Field label="Paid by"><Input value={form.paid_by || ""} onChange={(e) => setForm({ ...form, paid_by: e.target.value })} /></Field>
      <div className="flex items-end">
        <FileUploader
          yacht_id={yacht_id}
          folder="receipts"
          accept="image/*,application/pdf"
          onUploaded={(path) => setForm({ ...form, receipt_path: path })}
          label={form.receipt_path ? "Receipt attached" : "Attach receipt"}
          testid="pc-receipt"
        />
      </div>
    </div>
  );
}

// ─── Custodian banner ────────────────────────────────────────────────────
function CustodianBanner({ custodianName, custodianId, isSenior, onChange, currentUserId }) {
  const youAreCustodian = !!custodianId && custodianId === currentUserId;
  return (
    <div className="mt-6 rounded-md border border-zinc-200 bg-white p-4 flex flex-col sm:flex-row sm:items-start gap-3" data-testid="pc-custodian-banner">
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <ShieldCheck className={`h-5 w-5 shrink-0 mt-0.5 ${custodianId ? "text-emerald-600" : "text-amber-500"}`} />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Cash custodian</div>
          <div className="mt-0.5 text-sm">
            {custodianId ? (
              <>
                <span className="font-medium">{custodianName || "—"}</span>
                {youAreCustodian && <span className="ml-2 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-full">you</span>}
                <div className="text-xs text-zinc-500 mt-0.5 sm:inline sm:mt-0 sm:ml-2">holds the float and signs off top-ups + expenses</div>
              </>
            ) : (
              <span className="text-amber-700">No custodian set yet — top-ups go to legacy approval until one is appointed.</span>
            )}
          </div>
        </div>
      </div>
      {isSenior && (
        <Button
          variant="outline" size="sm"
          onClick={onChange}
          className="self-stretch sm:self-auto"
          data-testid="pc-change-custodian"
        >
          <UserCog className="h-4 w-4 mr-1.5" />
          {custodianId ? "Change" : "Appoint"}
        </Button>
      )}
    </div>
  );
}

// ─── Custodian picker dialog ─────────────────────────────────────────────
function CustodianDialog({ open, onClose, yachtId, currentCustodianId, onSaved }) {
  const [members, setMembers] = useState([]);
  const [selected, setSelected] = useState(currentCustodianId || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !yachtId) return;
    (async () => {
      try {
        const { data } = await api.get(`/yachts/${yachtId}/members`);
        // Only confirmed members can be appointed — pending invitees don't
        // yet exist as actionable accounts on this yacht.
        setMembers((data || []).filter((m) => !m.pending));
        setSelected(currentCustodianId || "");
      } catch { setMembers([]); }
    })();
  }, [open, yachtId, currentCustodianId]);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/petty-cash/settings`, {
        yacht_id: yachtId,
        custodian_id: selected || null,
      });
      toast.success(selected ? "Custodian updated" : "Custodian cleared");
      onSaved?.(data);
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="pc-custodian-dialog">
        <DialogHeader><DialogTitle>Cash custodian</DialogTitle></DialogHeader>
        <p className="text-sm text-zinc-600">
          The custodian holds the physical float for this yacht. They acknowledge top-ups when handed cash
          and can approve crew expenses (Owner/Manager retain the same approval power). Captain is the
          common choice — most yachts run that way.
        </p>
        <Field label="Appointed crew member">
          <select
            className="h-9 w-full rounded-md border border-input px-3 text-sm"
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            data-testid="pc-custodian-select"
          >
            <option value="">— none —</option>
            {members.map((m) => (
              <option key={m.user_id} value={m.user_id}>
                {(m.name || m.email)} · {m.role}
              </option>
            ))}
          </select>
        </Field>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} className="bg-ink hover:bg-black" disabled={busy} data-testid="pc-custodian-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Details Modal ───────────────────────────────────────────────────────
function PettyCashDetailDialog({ entry, onClose, canDelete, onDelete, canEdit, onEdit }) {
  const [tab, setTab] = useState("details");
  const [auditRows, setAuditRows] = useState(null);
  // Full-screen zoom for the receipt image — opened by clicking the image.
  const [lightbox, setLightbox] = useState(null);

  // Reset to the Details tab whenever a different entry is opened, and
  // forget any audit rows from the previous entry. The History tab itself
  // lazy-fetches on first view (see effect below).
  useEffect(() => {
    setTab("details");
    setAuditRows(null);
  }, [entry?.id]);

  useEffect(() => {
    if (!entry || tab !== "history" || auditRows !== null) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/audit", { params: { ref_kind: "petty_cash", ref_id: entry.id } });
        if (alive) setAuditRows(Array.isArray(data) ? data : []);
      } catch (e) {
        if (alive) {
          toast.error(fmtErr(e));
          setAuditRows([]);  // stop the spinner, show empty state
        }
      }
    })();
    return () => { alive = false; };
  }, [entry?.id, tab, auditRows]);

  if (!entry) return null;
  const dateStr = (entry.date || entry.created_at || "").slice(0, 10) || "—";
  const isImageReceipt = entry.receipt_path && /\.(png|jpe?g|webp|heic|heif|gif)$/i.test(entry.receipt_path);
  const isPdfReceipt = entry.receipt_path && /\.pdf$/i.test(entry.receipt_path);
  const ackLabel = entry.type === "topup"
    ? (entry.status === "received" ? "Received by" : entry.status === "rejected" ? "Rejected by" : null)
    : (entry.status === "approved" ? "Approved by" : entry.status === "rejected" ? "Rejected by" : null);
  return (
    <Dialog open={!!entry} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="w-[calc(100vw-1rem)] sm:max-w-2xl max-h-[calc(100dvh-1rem)] overflow-y-auto p-4 sm:p-6"
        data-testid="pc-detail-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 flex-wrap pr-6">
            <span>Petty cash entry</span>
            <StatusPill value={entry.status} />
            {entry.updated_at && entry.updated_at !== entry.created_at && (
              <span className="text-[10px] uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded">
                edited
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="text-[12px] sm:text-sm">
            {entry.type === "topup" ? "Custodian top-up" : "Expense"} · {entry.currency} {Number(entry.amount).toFixed(2)}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="mt-1">
          <TabsList className="w-full">
            <TabsTrigger value="details" data-testid="pc-tab-details" className="flex-1">Details</TabsTrigger>
            <TabsTrigger value="history" data-testid="pc-tab-history" className="flex-1">History</TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="mt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
              <DetailRow label="Date">{dateStr}</DetailRow>
              <DetailRow label="Type">
                {entry.type === "topup"
                  ? <span className="text-emerald-700">Top-up</span>
                  : <span>Expense</span>}
              </DetailRow>
              <DetailRow label="Category">{entry.category || "—"}</DetailRow>
              <DetailRow label="Amount">
                <span className="font-mono">{entry.currency} {Number(entry.amount).toFixed(2)}</span>
              </DetailRow>
              <DetailRow label="Paid by">{entry.paid_by || "—"}</DetailRow>
              <DetailRow label="Submitted by">{entry.created_by_name || "—"}</DetailRow>
              <DetailRow label="Description" className="sm:col-span-2">
                <span className="whitespace-pre-wrap">{entry.description || "—"}</span>
              </DetailRow>
              {ackLabel && entry.approved_by_name && (
                <DetailRow label={ackLabel} className="sm:col-span-2">
                  {entry.approved_by_name}
                  {entry.approved_at && (
                    <span className="ml-2 text-xs text-zinc-500">
                      · {entry.approved_at.slice(0, 10)}
                    </span>
                  )}
                </DetailRow>
              )}
            </div>
            {entry.receipt_path && (
              <div className="mt-2 border-t border-zinc-200 pt-4">
                <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">Receipt</div>
                {isImageReceipt ? (
                  <button
                    type="button"
                    onClick={() => setLightbox({ paths: [entry.receipt_path], index: 0, alt: "Receipt" })}
                    className="block cursor-zoom-in"
                    title="Click to view full size"
                    data-testid="pc-receipt-image"
                  >
                    <AuthImage
                      path={entry.receipt_path}
                      alt="Receipt"
                      className="max-h-72 w-auto rounded-md border border-zinc-200"
                    />
                  </button>
                ) : isPdfReceipt ? (
                  <div className="space-y-2">
                    <div className="max-h-72 overflow-y-auto rounded-md max-w-full">
                      <PdfPreview path={entry.receipt_path} width={280} testid="pc-receipt-pdf" />
                    </div>
                    <div>
                      <AuthFileLink
                        path={entry.receipt_path}
                        className="text-xs"
                        testid="pc-receipt-open"
                      >
                        Open full PDF →
                      </AuthFileLink>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm">
                    <AuthFileLink
                      path={entry.receipt_path}
                      testid="pc-receipt-open"
                    >
                      {entry.receipt_path.split("/").pop() || "Open receipt"}
                    </AuthFileLink>
                    <div className="text-xs text-zinc-500 mt-1">
                      Opens in a new tab — your browser will preview the file or download it.
                    </div>
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="history" className="mt-4">
            <AuditTimeline rows={auditRows} emptyText="No edits or status changes yet." testidPrefix="pc-history" />
          </TabsContent>
        </Tabs>

        <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-between gap-2 mt-3">
          <Button variant="outline" onClick={onClose} className="w-full sm:w-auto" data-testid="pc-detail-close">Close</Button>
          {tab === "details" && (
            <div className="flex flex-col sm:flex-row gap-2">
              {canDelete && (
                <Button
                  variant="outline"
                  onClick={onDelete}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 w-full sm:w-auto"
                  data-testid="pc-detail-delete"
                >
                  <Trash2 className="h-4 w-4 mr-2" />Delete entry
                </Button>
              )}
              {canEdit && onEdit && (
                <Button
                  onClick={onEdit}
                  className="bg-ink hover:bg-black w-full sm:w-auto"
                  data-testid="pc-detail-edit"
                >
                  <Pencil className="h-4 w-4 mr-2" />Edit
                </Button>
              )}
            </div>
          )}
        </DialogFooter>
        <ImageLightbox state={lightbox} onClose={() => setLightbox(null)} />
      </DialogContent>
    </Dialog>
  );
}

function DetailRow({ label, children, className = "" }) {
  return (
    <div className={className}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
      <div className="mt-1 text-zinc-900">{children}</div>
    </div>
  );
}
